import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { useIdentityStore } from '@/stores/identity.store';
import { mediaUrl } from '@/lib/media';
import { otherLocale, useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { Alert, Button, Field, Input, cx } from '@/components/ui';
import { StoreMark } from '@/components/store-mark';
import { CartIcon, ChartIcon, BoxIcon, ShieldIcon } from '@/components/icons';
import type { MessageKey } from '@/i18n/messages';
import { CodeForm, RecoveryCodes, ScanCode } from '@/components/two-factor';

/** What the brand side promises, one line each. */
const FEATURES: { label: MessageKey; Icon: (props: { className?: string }) => React.ReactElement }[] = [
  { label: 'signInFeatureOrders', Icon: CartIcon },
  { label: 'signInFeatureCatalog', Icon: BoxIcon },
  { label: 'signInFeatureInsights', Icon: ChartIcon },
];

/**
 * Sign-in. Staff and owner only — a customer account is turned away here.
 *
 * Two halves on a desktop (ADR-123): the store's own mark on the brand's ground,
 * and the form. The store's name and logo come from its public settings, so the
 * client's brand greets their team before anyone has signed in; never ours.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, submitting, error, clearError, challenge, setup, recoveryCodes, verifyCode, startSetup, finishSetup, continueAfterCodes, cancelChallenge } =
    useAuthStore();
  const { locale, setLocale } = useUiStore();
  const t = useT();
  const identity = useIdentityStore();
  const loadIdentity = useIdentityStore((state) => state.load);
  const storeName = identity.name || t('appName');
  const logo = mediaUrl(identity.logoUrl);

  useEffect(() => {
    void loadIdentity();
  }, [loadIdentity]);

  useEffect(() => {
    document.title = identity.name ? `${identity.name} · ${t('appName')}` : t('appName');
  }, [identity.name, t]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    clearError();

    if (await signIn(email, password)) enter();
  }

  // Back to whatever they were trying to reach, or orders — the screen the
  // store is run from.
  function enter() {
    const from = (location.state as { from?: string } | null)?.from ?? '/orders';
    navigate(from, { replace: true });
  }

  // The second step (ADR-116), once the password was right.
  const step = recoveryCodes ? 'codes' : challenge?.step === 'verify' ? 'verify' : challenge?.step === 'setup' ? 'setup' : 'password';
  const heading = { password: t('signIn'), verify: t('twoFactorTitle'), setup: t('twoFactorSetupTitle'), codes: t('twoFactorCodesTitle') }[step];
  const subheading = { password: t('signInHint'), verify: t('twoFactorVerifyHint'), setup: t('twoFactorRequiredHint'), codes: t('twoFactorCodesKeep') }[step];

  return (
    <div className="grid min-h-dvh bg-surface lg:grid-cols-2">
      {/* The store's side: its own mark on the brand's ground (ADR-123). */}
      <aside className="brand-ground relative hidden flex-col justify-between overflow-hidden p-10 text-nav-ink lg:flex xl:p-14">
        <div className="flex items-center gap-3.5">
          <StoreMark logo={logo} name={storeName} size="lg" />
          <div className="min-w-0">
            <p dir="auto" className="truncate text-lg font-semibold">
              {storeName}
            </p>
            <p className="text-sm text-nav-faint">{t('appName')}</p>
          </div>
        </div>

        <div className="max-w-md">
          <h2 className="text-[2.25rem] leading-[1.2] font-bold tracking-tight text-balance">{t('signInWelcomeTitle')}</h2>
          <p className="mt-4 text-base leading-relaxed text-nav-muted">{t('signInWelcomeBody')}</p>
          <ul className="mt-9 flex flex-col gap-3.5">
            {FEATURES.map(({ label, Icon }) => (
              <li key={label} className="flex items-center gap-3 text-sm font-medium">
                <span className="inline-flex size-10 items-center justify-center rounded-xl bg-white/[0.06] text-brand-400 ring-1 ring-white/10">
                  <Icon className="size-5" />
                </span>
                {t(label)}
              </li>
            ))}
          </ul>
        </div>

        <p className="flex items-center gap-2 text-xs text-nav-faint">
          <ShieldIcon className="size-4" />
          {t('signInSecure')}
        </p>
      </aside>

      <main className="flex flex-col px-5 py-5 sm:px-10">
        <div className="flex items-center justify-between gap-3">
          {/* On a phone the brand side is gone; the mark comes with the form. */}
          <div className="flex min-w-0 items-center gap-2.5 lg:invisible">
            <StoreMark logo={logo} name={storeName} size="sm" />
            <span dir="auto" className="truncate text-sm font-semibold text-ink">
              {storeName}
            </span>
          </div>

          {/* Language is switchable before signing in — the person at the
              keyboard may not read the default. */}
          <button
            type="button"
            onClick={() => setLocale(otherLocale(locale))}
            aria-label={t('toggleLanguage')}
            className="inline-flex h-9 shrink-0 items-center rounded-control bg-ink/[0.05] p-0.5 text-xs"
          >
            {(['en', 'ar'] as const).map((option) => (
              <span
                key={option}
                className={cx(
                  'rounded-[calc(var(--radius-control)-3px)] px-2.5 py-1 font-semibold transition-colors',
                  locale === option ? 'bg-surface text-ink shadow-card' : 'text-ink-muted',
                )}
              >
                {option === 'en' ? 'EN' : 'ع'}
              </span>
            ))}
          </button>
        </div>

        <div className="flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-[22rem]">
            <h1 className="text-[1.75rem] leading-tight font-bold tracking-tight text-ink">{heading}</h1>
            <p className="mt-2 text-sm text-ink-muted">{subheading}</p>

            <div className="mt-8">
        {step === 'verify' ? (
          <div className="flex flex-col gap-4">
            <CodeForm
              submit={async (code) => (await verifyCode(code)) && enter()}
              submitting={submitting}
              label={t('twoFactorCode')}
              action={submitting ? t('signingIn') : t('twoFactorVerify')}
              recovery
            />
            {error ? <Alert>{error}</Alert> : null}
            <Button variant="ghost" onClick={cancelChallenge}>
              {t('twoFactorBack')}
            </Button>
          </div>
        ) : step === 'setup' ? (
          <div className="flex flex-col gap-4">
            {setup ? (
              <>
                <ScanCode setup={setup} />
                <CodeForm submit={finishSetup} submitting={submitting} label={t('twoFactorFirstCode')} action={t('twoFactorTurnOn')} />
              </>
            ) : (
              <Button onClick={() => void startSetup()} disabled={submitting}>
                {t('twoFactorStart')}
              </Button>
            )}
            {error ? <Alert>{error}</Alert> : null}
            <Button variant="ghost" onClick={cancelChallenge}>
              {t('twoFactorBack')}
            </Button>
          </div>
        ) : step === 'codes' && recoveryCodes ? (
          <div className="flex flex-col gap-4">
            <RecoveryCodes codes={recoveryCodes} />
            <Button
              onClick={() => {
                continueAfterCodes();
                enter();
              }}
            >
              {t('twoFactorCodesSaved')}
            </Button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <Field label={t('email')} htmlFor="email">
              <Input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                autoFocus
              />
            </Field>
  
            <Field label={t('password')} htmlFor="password">
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </Field>
  
            {/* The API answers the same way for a wrong email and a wrong
                password, so this message cannot be used to discover accounts. */}
            {error ? <Alert>{error}</Alert> : null}
  
            <Button type="submit" disabled={submitting} className="mt-2 min-h-11 w-full">
              {submitting ? t('signingIn') : t('signIn')}
            </Button>
          </form>
        )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
